import { Crown, GraduationCap, Quote, ArrowRight, BookOpen, Scale, Users } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

const PHOTO_URL = "/images/founder-almeer-nance.jpg";

const CERTS = ["CIT", "HVAC", "TCOM", "CBIP", "CEC"];

export default function Founder() {
  return (
    <div className="bg-tcpi-bg" data-testid="founder-page">
      <Seo
        path="/founder"
        title="Almeer Nance — Founder & Executive Director"
        description="Almeer Nance, founder of The Crown Path Initiative, built TCPI from three decades of lived experience inside the Tennessee criminal justice system — earning college degrees, multiple vocational and leadership certifications, and serving as a YAOP mentor."
        type="profile"
        image="https://thecrownpathinitiative.org/images/founder-almeer-nance.jpg"
      />

      {/* SEARCH-VISITOR LANDING CTA — quick on-ramp to the homepage so people
          arriving from Google can see the program at a glance. */}
      <section
        className="border-b border-[#C9A961]/15 bg-[#121110]/40"
        data-testid="founder-search-cta"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-[#D1CFC9]">
            New to <span className="text-[#C9A961] font-semibold">The Crown Path Initiative?</span>{" "}
            <span className="text-[#8A8782]">Start here to see the full program.</span>
          </p>
          <Link
            to="/"
            className="btn-primary inline-flex items-center justify-center gap-2 text-sm whitespace-nowrap"
            data-testid="founder-cta-visit-home"
          >
            Explore the program
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* HERO */}
      <section className="relative py-20 md:py-28 grain">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2 fade-up">
            <div className="relative">
              <div className="absolute -inset-3 bg-[#C9A961]/20 blur-2xl rounded-2xl" />
              <img
                src={PHOTO_URL}
                alt="Almeer Nance — founder of The Crown Path Initiative, photographed in cap and gown holding his college diploma"
                className="relative rounded-2xl border border-[#C9A961]/30 shadow-2xl w-full object-cover"
                data-testid="founder-photo"
              />
            </div>
            <div className="mt-5 text-center">
              <div className="overline mb-2">In his cap & gown</div>
              <p className="text-sm text-[#8A8782] italic">
                College graduation — completed entirely while incarcerated.
              </p>
            </div>
          </div>

          <div className="lg:col-span-3 fade-up" style={{ animationDelay: "150ms" }}>
            <div className="overline mb-5">Founder & Executive Director</div>
            <h1
              className="text-5xl md:text-6xl text-[#F7F5F0] leading-[1.05] mb-5"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
              data-testid="founder-name"
            >
              Almeer Nance
            </h1>
            <p className="text-lg text-[#C9A961] italic mb-8" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Lived experience. Turned into leadership.
            </p>

            <div className="space-y-5 text-[#D1CFC9] leading-relaxed">
              <p>
                Born into poverty and raised in environments shaped by adversity, violence,
                instability, and limited opportunity, <strong className="text-[#F7F5F0]">Almeer Nance</strong> experienced
                the realities of the criminal justice system at an early age. As a juvenile,
                he was charged and ultimately tried as an adult, receiving a life sentence
                plus 25 years in the Tennessee Department of Correction.
              </p>
              <p>
                Rather than allowing incarceration to define his future, he committed himself
                to personal transformation, education, leadership development, and rehabilitation.
                During more than three decades of incarceration, he completed every educational
                and rehabilitative opportunity available to him — earning college degrees along
                with multiple vocational and leadership certifications, including CIT, HVAC, TCOM,
                CBIP, CEC, and several additional program certifications focused on mentorship,
                communication, behavioral growth, and personal development.
              </p>
              <p>
                Over the years, he became deeply involved in mentorship and rehabilitation
                initiatives, eventually serving as a mentor within the YAOP program, where he
                has helped guide younger incarcerated individuals toward accountability,
                emotional growth, discipline, and positive life change.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2" data-testid="founder-certs">
              {CERTS.map((c) => (
                <span
                  key={c}
                  className="px-3 py-1 text-xs border border-[#C9A961]/40 text-[#C9A961] rounded-full tracking-wider"
                >
                  {c}
                </span>
              ))}
              <span className="px-3 py-1 text-xs border border-[#C9A961]/40 text-[#C9A961] rounded-full tracking-wider">
                + additional certifications
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="bg-[#121110] py-20 md:py-24 relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Quote className="w-10 h-10 text-[#C9A961] mx-auto mb-6" strokeWidth={1.2} />
          <p
            className="text-3xl md:text-4xl text-[#F7F5F0] leading-snug"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
            data-testid="founder-pullquote"
          >
            "People are more than the worst decision they have made — but real transformation
            requires accountability, education, discipline, emotional growth, and
            <span className="text-[#C9A961] italic"> intentional effort.</span>"
          </p>
          <div className="gold-divider w-32 mx-auto my-10" />
          <p className="text-sm text-[#8A8782] tracking-wider uppercase">— Almeer Nance, Founder</p>
        </div>
      </section>

      {/* BUILDING THE INITIATIVE */}
      <section className="py-20 md:py-28 grain">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="overline mb-4">How TCPI Was Built</div>
          <h2
            className="text-3xl md:text-4xl text-[#F7F5F0] mb-8 max-w-3xl"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            A curriculum drawn from real life, real study, and real mentorship.
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="card-bento" data-testid="founder-pillar-experience">
              <Users className="w-6 h-6 text-[#C9A961] mb-4" strokeWidth={1.5} />
              <h3 className="text-xl text-[#C9A961] mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Lived experience
              </h3>
              <p className="text-[#D1CFC9] text-sm leading-relaxed">
                Decades inside the system gave Almeer an unflinching view of what works,
                what fails, and what justice-impacted people actually need to grow.
              </p>
            </div>
            <div className="card-bento" data-testid="founder-pillar-education">
              <GraduationCap className="w-6 h-6 text-[#C9A961] mb-4" strokeWidth={1.5} />
              <h3 className="text-xl text-[#C9A961] mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Education & study
              </h3>
              <p className="text-[#D1CFC9] text-sm leading-relaxed">
                College degrees, trauma-informed learning, leadership training, and a deep
                study of cognitive-behavioral and rehabilitation research.
              </p>
            </div>
            <div className="card-bento" data-testid="founder-pillar-mentorship">
              <BookOpen className="w-6 h-6 text-[#C9A961] mb-4" strokeWidth={1.5} />
              <h3 className="text-xl text-[#C9A961] mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Years of mentorship
              </h3>
              <p className="text-[#D1CFC9] text-sm leading-relaxed">
                As a YAOP mentor, he guided younger incarcerated individuals toward
                accountability, emotional growth, and positive life change.
              </p>
            </div>
          </div>

          <div className="space-y-5 text-[#D1CFC9] leading-relaxed max-w-3xl">
            <p>
              Drawing from lived experience, educational development, trauma-informed
              learning, leadership training, and years of mentorship work, Almeer
              developed <strong className="text-[#F7F5F0]">The Crown Path Initiative</strong> — a structured
              transformational curriculum focused on emotional awareness, accountability,
              leadership development, education, rehabilitation, and long-term personal
              growth for justice-impacted individuals.
            </p>
            <p>
              He is also the author of a <strong className="text-[#F7F5F0]">forthcoming book</strong> centered on survival,
              transformation, financial literacy, and breaking generational cycles — while
              actively advocating for criminal justice reform and rehabilitation-centered
              policy development throughout the state of Tennessee.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-[#121110] py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-2">
            <Scale className="w-12 h-12 text-[#C9A961] mb-6" strokeWidth={1.2} />
            <div className="overline mb-3">His Mission</div>
            <h2
              className="text-3xl md:text-4xl text-[#F7F5F0] leading-snug"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Lived experience, turned into leadership.
            </h2>
          </div>
          <div className="md:col-span-3 space-y-5 text-[#D1CFC9] leading-relaxed">
            <p>
              Today, Almeer's work is focused on helping others recognize that
              <em className="text-[#F7F5F0]"> people are more than the worst decision they have made</em> —
              while also emphasizing that true transformation requires accountability,
              education, discipline, emotional growth, and intentional effort.
            </p>
            <p className="text-xl text-[#F7F5F0]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              His mission is simple: to turn lived experience into{" "}
              <span className="text-[#C9A961] italic">leadership, restoration, and meaningful change</span>{" "}
              for future generations.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 grain">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Crown className="w-12 h-12 text-[#C9A961] mx-auto mb-6" strokeWidth={1.2} />
          <h2
            className="text-3xl md:text-4xl text-[#F7F5F0] mb-6"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Walk the path he built.
          </h2>
          <p className="text-[#D1CFC9] mb-10">
            Whether you are joining as a participant, partnering as an institution, or
            standing with us as a supporter — the Crown Path begins with one choice.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="founder-cta-enroll">
              Enroll Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/support" className="btn-secondary inline-flex items-center gap-2" data-testid="founder-cta-support">
              Support the Mission
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
